import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useAuth, useFirestore, useUserCompany } from '../hooks/useFirebase';

export const name = 'getResult';
export const description = 'Retrieves the results (eredmény) statistics for a project number directly from the database.';
export const parameters = {
  projectNumber: 'Optional project number (e.g., "M-22-04") to query. If not provided, it falls back to the currently selected project or the user\'s first available project.'
};
export const outputTextForUI = 'Eredmény lekérése';
export const useOnMobile = true;

export async function getResult(args?: { projectNumber?: string }) {
  const db = useFirestore();
  const company = await useUserCompany();
  const user = useAuth();

  let selectedProj = args?.projectNumber;

  // Fallback if no project number is provided
  if (!selectedProj) {
    const userRef = doc(db, 'user', user.uid);
    const userSnap = await getDoc(userRef);
    const projectNumbers = userSnap.data()?.project_numbers || [];
    if (projectNumbers.length > 0) {
      selectedProj = projectNumbers[0];
    }
  }

  if (!selectedProj) {
    throw new Error('Nem található munkaszám. Kérlek adj meg egy érvényes munkaszámot.');
  }

  // 1. Fetch data in parallel from Firestore for calculations
  const [ownSnapshot, dailySnapshot, subSnapshot, projectSnapshot] = await Promise.all([
    getDocs(query(collection(db, 'contract'), where('contractor', '==', company), where('project_number', '==', selectedProj))),
    getDocs(query(collection(db, 'daily'), where('company', '==', company), where('project_number', '==', selectedProj))),
    getDocs(query(collection(db, 'contract'), where('customer', '==', company), where('project_number', '==', selectedProj))),
    getDocs(query(collection(db, 'project'), where('company', '==', company), where('project_number', '==', selectedProj)))
  ]);

  // 2. Perform CostResult calculations
  let actualIncomeTotal = 0;
  ownSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data && typeof data.forint_readiness === 'number') {
      actualIncomeTotal += data.forint_readiness;
    }
  });
  actualIncomeTotal = Math.round(actualIncomeTotal);

  let dailyCostTotal = 0;
  dailySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data && typeof data.final_price === 'number') {
      dailyCostTotal += data.final_price;
    }
  });

  let subcontractorContractsReadiness = 0;
  subSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data && typeof data.forint_readiness === 'number') {
      subcontractorContractsReadiness += data.forint_readiness;
    }
  });

  const actualCostTotal = Math.round(dailyCostTotal + subcontractorContractsReadiness);
  const partialResultTotal = actualIncomeTotal - actualCostTotal;

  // Retrieve overhead and other costs from the project config document
  let overheadPercentage = 0;
  let otherCosts = 0;
  projectSnapshot.forEach((doc) => {
    const data = doc.data();
    if (data) {
      if (typeof data.overhead_percentage === 'number') {
        overheadPercentage = data.overhead_percentage;
      }
      if (typeof data.other_costs === 'number') {
        otherCosts = data.other_costs;
      }
    }
  });

  const overheadForint = Number(Math.abs(Number(partialResultTotal) / 100 * Number(overheadPercentage)).toFixed(0));
  const allOtherCostsTotal = otherCosts + overheadForint;
  const resultForintTotal = actualIncomeTotal - actualCostTotal - allOtherCostsTotal;

  const fixedActualIncomeTotal = actualIncomeTotal === 0 ? 1 : actualIncomeTotal;
  const resultPercentageTotal = Number((resultForintTotal / fixedActualIncomeTotal * 100).toFixed(2));

  return {
    success: true,
    projectNumber: selectedProj,
    metrics: {
      actualIncome: actualIncomeTotal,
      actualCost: actualCostTotal,
      partialResult: partialResultTotal,
      overheadPercentage,
      overheadForint,
      otherCosts,
      allOtherCosts: allOtherCostsTotal,
      resultForint: resultForintTotal,
      resultPercentage: resultPercentageTotal
    }
  };
}
